/**
 * Mailtexte: Vorlage der Agentur (org_texts) oder des Produkts (platform_texts) mit Werten füllen.
 * Werte werden HTML-escaped; bleibt ein Platzhalter offen, geht die Mail nicht raus (kein halber Text).
 *
 * Bedingte Textteile (0041): `{{#deal.title}} … {{/deal.title}}` erscheint nur, wenn der Wert da ist,
 * `{{^deal.title}} … {{/deal.title}}` nur, wenn er fehlt. So kommt eine Rechnung ohne Kooperation, eine Anforderung
 * ohne Notiz oder ein Termin ohne Uhrzeit ohne offenen Platzhalter aus. Bedingungen dürfen auch Merkmale
 * (MAIL_FLAGS, z. B. `payment.by_agency`) prüfen; Merkmale selbst werden nie eingesetzt.
 * Dieselbe Logik steht in SQL (app.mail_missing_keys, 0041), damit schon beim Einreihen geprüft wird.
 */
import { formatContractDate, formatContractMonth } from "./contract-render.js";
import { formatMoney, isCurrency } from "./money.js";
import type { Language, MoneyLocale } from "./types.js";

/** Erlaubte Platzhalter der Mailtexte (Kopf von 0008_texte.sql, 0015, 0035, 0041). */
export const MAIL_PLACEHOLDERS = [
  "agency.name", "talent.first_name", "brand.name", "invoice.number", "invoice.amount", "invoice.due_date",
  "invoice.days_overdue", "dunning.fee", "dunning.deadline", "contract.title", "link", "deal.title", "period.month",
  "reason", "event.title", "event.date", "task.title", "task.detail", "member.first_name",
  // 0041: Termine mit Uhrzeit, Ort und Beschreibung; Chat-Sammelmail; Erinnerungen vor Fristen
  "event.time", "event.location", "event.meeting_url", "event.description", "chat.count", "chat.from", "chat.preview", "deadline.date", "deadline.days",
  // 0043: Mahnung einer Creator-Forderung im Auftrag des Creators
  "creditor.name",
  // 0056: bisheriger Termin, bisherige Deadline, tägliche Sammelmail
  "event.previous", "deadline.previous", "task.count", "task.list",
  // 0072: Angebot an die Marke (Nummer, Titel der Anfrage, gültig bis, Summe) und Ansprechperson der Marke
  "offer.number", "offer.title", "offer.valid_until", "offer.amount", "contact.name",
  // 0076: Mahnung nach Teilzahlung: eingegangener Betrag (nur nach einer Teilzahlung) und offener Rest
  "invoice.paid_amount", "invoice.open_amount",
  // 0106 (SaaS-Tauglichkeit A-06, R-02): Portalname der Agentur (org_settings.product_name_override), sonst
  // „das Portal deiner Agentur“; immer gefüllt, damit Creator-Mails nie fest „CreatorOffice“ sagen müssen
  "portal.name",
  // 0238 (Praxistest K-140): Bezeichnung der Mahnstufe (auch eigene Stufen wie „4. Mahnung“) und Verzugszinsen dieser Mahnung
  "dunning.stage", "dunning.interest",
  // 0286 (K-405): Bezeichnung der Gebühr wie in der Forderungsaufstellung (Verzugspauschale mit Rechtsgrundlage, Mahngebühr …)
  "dunning.fee_label",
  // 0305 (K-420): Gebühr als Satzteil mit Artikel und Betrag („die Verzugspauschale von 40,00 € (§ 288 Abs. 5 BGB)“,
  // „a €40.00 flat late-payment fee (section 288 (5) BGB)“), damit Sätze wie „Zusätzlich berechnen wir …“ grammatisch stimmen
  "dunning.fee_phrase",
] as const;
export type MailPlaceholder = (typeof MAIL_PLACEHOLDERS)[number];

/**
 * Klartext je Platzhalter (C-05, 0106): Der Vorlagen-Editor zeigt Platzhalter als Knöpfe mit diesen Namen statt als
 * `{{talent.first_name}}`. Die Schlüssel bleiben technisch („talent“, „deal“), die Beschriftung sagt Creator und Kooperation.
 */
export const MAIL_PLACEHOLDER_LABELS: Readonly<Record<MailPlaceholder, { de: string; en: string }>> = {
  "agency.name": { de: "Name der Agentur", en: "Agency name" },
  "talent.first_name": { de: "Vorname des Creators", en: "Creator's first name" },
  "brand.name": { de: "Name der Marke", en: "Brand name" },
  "invoice.number": { de: "Rechnungsnummer", en: "Invoice number" },
  "invoice.amount": { de: "Rechnungsbetrag", en: "Invoice amount" },
  "invoice.due_date": { de: "Fälligkeitsdatum", en: "Due date" },
  "invoice.days_overdue": { de: "Tage überfällig", en: "Days overdue" },
  "dunning.fee": { de: "Mahngebühr", en: "Dunning fee" },
  "dunning.deadline": { de: "Neue Zahlungsfrist", en: "New payment deadline" },
  "contract.title": { de: "Titel des Vertrags", en: "Contract title" },
  link: { de: "Link ins Portal", en: "Link to the portal" },
  "deal.title": { de: "Titel der Kooperation", en: "Deal title" },
  "period.month": { de: "Abrechnungsmonat", en: "Billing month" },
  reason: { de: "Grund oder Notiz", en: "Reason or note" },
  "event.title": { de: "Titel des Termins", en: "Appointment title" },
  "event.date": { de: "Datum", en: "Date" },
  "task.title": { de: "Aufgabe", en: "Task" },
  "task.detail": { de: "Einzelheiten der Aufgabe", en: "Task details" },
  "member.first_name": { de: "Vorname im Team", en: "Team member's first name" },
  "event.time": { de: "Uhrzeit", en: "Time" },
  "event.location": { de: "Ort", en: "Location" },
  // 0396 (K-559 c): Link für Calls
  "event.meeting_url": { de: "Link für Calls", en: "Call link" },
  "event.description": { de: "Beschreibung des Termins", en: "Appointment description" },
  "chat.count": { de: "Anzahl ungelesener Nachrichten", en: "Number of unread messages" },
  "chat.from": { de: "Absender der Nachricht", en: "Message sender" },
  "chat.preview": { de: "Vorschau der Nachricht", en: "Message preview" },
  "deadline.date": { de: "Frist", en: "Deadline" },
  "deadline.days": { de: "Tage bis zur Frist", en: "Days until the deadline" },
  "creditor.name": { de: "Gläubiger (Creator)", en: "Creditor (creator)" },
  "dunning.stage": { de: "Mahnstufe", en: "Dunning level" },
  "dunning.interest": { de: "Verzugszinsen", en: "Default interest" },
  "dunning.fee_label": { de: "Bezeichnung der Gebühr", en: "Fee description" },
  "dunning.fee_phrase": { de: "Gebühr mit Betrag (Satzteil)", en: "Fee with amount (phrase)" },
  "event.previous": { de: "Bisheriger Termin", en: "Previous date" },
  "deadline.previous": { de: "Bisherige Frist", en: "Previous deadline" },
  "task.count": { de: "Anzahl der Aufgaben", en: "Number of tasks" },
  "task.list": { de: "Liste der Aufgaben", en: "List of tasks" },
  "offer.number": { de: "Angebotsnummer", en: "Offer number" },
  "offer.title": { de: "Titel des Angebots", en: "Offer title" },
  "offer.valid_until": { de: "Angebot gültig bis", en: "Offer valid until" },
  "offer.amount": { de: "Angebotssumme", en: "Offer total" },
  "contact.name": { de: "Ansprechperson der Marke", en: "Brand contact" },
  "invoice.paid_amount": { de: "Bereits gezahlt", en: "Already paid" },
  "invoice.open_amount": { de: "Offener Betrag", en: "Open amount" },
  "portal.name": { de: "Portalname der Agentur", en: "Agency portal name" },
};
/** Klartext eines Platzhalters; unbekannte Schlüssel bleiben sichtbar. */
export function mailPlaceholderLabel(key: string, lang: "de" | "en" = "de"): string {
  const l = (MAIL_PLACEHOLDER_LABELS as Record<string, { de: string; en: string }>)[key];
  return l ? l[lang] : key;
}

/** Merkmale nur für bedingte Textteile (Ja/Nein, kein Text). */
export const MAIL_FLAGS = [
  /** Die Agentur zahlt den Creator selbst aus (Rechnung an die Agentur oder Auszahlung schon bestätigt) */
  "payment.by_agency",
  /** Die Anforderung bringt vorbereitete Positionen mit */
  "request.items",
  /** Anforderung vor Ende des Monats (Vorkasse, Abschlag) */
  "request.advance",
  /** Vertrag wurde außerhalb des Portals unterschrieben und hochgeladen */
  "contract.external",
  /** Empfänger hat ein Konto im Portal (Knopf und Link sinnvoll) */
  "recipient.account",
  /** 0087: Steuerbegriffe der Schweiz bzw. Liechtensteins (MWST, „nicht mehrwertsteuerpflichtig“), Parameter vat_country CH oder LI */
  "tax.ch",
  /** K-282: Agentur in der Schweiz bzw. in Liechtenstein (Betreibung statt Inkasso), Parameter agency_country */
  "agency.ch",
  /** 0286 (K-408): Agentur in Deutschland (gerichtliches Mahnverfahren) bzw. in Österreich (Mahnverfahren nach der ZPO) */
  "agency.de",
  "agency.at",
  /** 0437: für die Agentur gilt nach Vertragsende noch die alte Regel (Nur-Lesen-Phase statt Sperre, keine Zustimmung zur AGB vom
   *  23.10.2026), Parameter old_terms_rule; die Mails zum Vertragsende nehmen dann den Wortlaut vor 0425 */
  "terms.old",
] as const;
export type MailFlag = (typeof MAIL_FLAGS)[number];
export type MailKey = MailPlaceholder | MailFlag;

export interface MailContext {
  agencyName: string;
  talentFirstName?: string | null;
  locale: MoneyLocale;
  language: Language;
  /** Link ins Portal; null bei Empfängern ohne Konto (Marken): dann gibt es keinen Link */
  link: string | null;
  /** Zeitzone des Empfängers für Uhrzeiten (Termine); ohne Wert die Zeitzone des Termins */
  recipientTimeZone?: string | null;
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));
const yes = (v: unknown) => v === true || v === "true" || v === 1 || v === "1";

/**
 * Uhrzeit eines Termins mit Zeitzone: „10:00–11:30 Uhr (Europe/Berlin)“. Liegt der Empfänger in einer anderen Zone,
 * kommt die umgerechnete Uhrzeit dazu („bei dir 09:00 Uhr, Europe/Lisbon“). Ohne Startzeit: null (ganztägig).
 */
export function eventTimeText(p: { date?: unknown; start_time?: unknown; end_time?: unknown; time_zone?: unknown }, language: Language, recipientZone?: string | null): string | null {
  const start = str(p.start_time)?.slice(0, 5) ?? null;
  if (!start || !/^\d{2}:\d{2}$/.test(start)) return null;
  const end = str(p.end_time)?.slice(0, 5) ?? null;
  const zone = str(p.time_zone);
  const suffix = language === "en" ? "" : " Uhr";
  let text = `${start}${end && /^\d{2}:\d{2}$/.test(end) ? `–${end}` : ""}${suffix}${zone ? ` (${zone})` : ""}`;
  const day = str(p.date)?.slice(0, 10);
  if (zone && recipientZone && recipientZone !== zone && day && /^\d{4}-\d{2}-\d{2}$/.test(day)) {
    const local = convertTime(day, start, zone, recipientZone);
    if (local && local.time !== start) {
      const other = local.day !== day ? `${local.day.slice(8, 10)}.${local.day.slice(5, 7)}. ` : "";
      text += language === "en" ? `, ${other}${local.time} your time (${recipientZone})` : `, bei dir ${other}${local.time} Uhr (${recipientZone})`;
    }
  }
  return text;
}

/** Wandzeit in Zone A → Wandzeit in Zone B (ohne Bibliothek, über Intl). */
export function convertTime(day: string, hhmm: string, fromZone: string, toZone: string): { day: string; time: string } | null {
  try {
    const [y, mo, d] = day.split("-").map(Number) as [number, number, number];
    const [h, mi] = hhmm.split(":").map(Number) as [number, number];
    const guess = Date.UTC(y, mo - 1, d, h, mi);
    const offset = (zone: string, at: number) => {
      const parts = new Intl.DateTimeFormat("en-CA", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
        .formatToParts(new Date(at));
      const get = (t: string) => Number(parts.find((x) => x.type === t)?.value);
      return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute")) - at;
    };
    const instant = guess - offset(fromZone, guess - offset(fromZone, guess));
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: toZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
      .formatToParts(new Date(instant));
    const get = (t: string) => parts.find((x) => x.type === t)?.value ?? "";
    return { day: `${get("year")}-${get("month")}-${get("day")}`, time: `${get("hour")}:${get("minute")}` };
  } catch {
    return null;
  }
}

/**
 * Portalname in Mailtexten (0106): der eigene Name der Agentur aus den Parametern (die Datenbank legt ihn beim Einreihen als
 * params.portal_name ab), sonst „das Portal deiner Agentur“ bzw. „your agency's portal“. Nie leer, nie „CreatorOffice“.
 */
export function portalNameText(name: unknown, language: Language): string {
  const own = typeof name === "string" ? name.trim() : "";
  if (own) return own;
  return language === "en" ? "your agency's portal" : "das Portal deiner Agentur";
}

/** Parameter einer Meldung (app.notify) in Mail-Werte übersetzen. */
export function mailVariables(params: Record<string, unknown>, ctx: MailContext): Partial<Record<MailKey, string>> {
  const out: Partial<Record<MailKey, string>> = { "agency.name": ctx.agencyName, "portal.name": portalNameText(params.portal_name, ctx.language) };
  const set = (k: MailKey, v: string | null) => {
    if (v !== null) out[k] = v;
  };
  set("link", str(ctx.link));
  set("talent.first_name", str(ctx.talentFirstName));
  set("invoice.number", str(params.number));
  const cents = params.amount_cents;
  if (cents !== undefined && cents !== null && isCurrency(params.currency)) {
    set("invoice.amount", formatMoney(Number(cents), params.currency, ctx.locale));
    // Teilzahlung (0076): offener Rest immer, eingegangener Betrag nur, wenn schon etwas gezahlt ist
    const paid = Math.max(0, Number(params.paid_cents ?? 0) || 0);
    set("invoice.open_amount", formatMoney(Math.max(0, Number(cents) - paid), params.currency, ctx.locale));
    if (paid > 0) set("invoice.paid_amount", formatMoney(paid, params.currency, ctx.locale));
  }
  // K-420: englische Mails nennen Rechnungs- und Mahndaten wie das Mahnschreiben („10 Aug 2026“), nicht 10/08/2026
  const rechnungsDatum = (v: string) => (ctx.language === "en" ? englishDate(v) : formatContractDate(v, ctx.locale));
  if (str(params.due_date)) set("invoice.due_date", rechnungsDatum(String(params.due_date)));
  set("deal.title", str(params.deal_title ?? params.title));
  set("contract.title", str(params.title));
  set("brand.name", str(params.brand));
  set("creditor.name", str(params.creditor));
  if (str(params.month ?? params.revenue_month)) set("period.month", formatContractMonth(String(params.month ?? params.revenue_month), ctx.locale));
  set("reason", str(params.reason ?? params.note));
  set("event.title", str(params.title));
  if (str(params.date)) set("event.date", formatContractDate(String(params.date), ctx.locale));
  set("event.time", eventTimeText(params, ctx.language, ctx.recipientTimeZone ?? null));
  set("event.location", str(params.location));
  set("event.meeting_url", str(params.meeting_url));
  set("event.description", str(params.description));
  // Mahnungen (0027): Tage seit Fälligkeit, neue Frist, Gebühr der Stufe
  if (params.days_overdue !== undefined && params.days_overdue !== null) set("invoice.days_overdue", String(Math.max(0, Number(params.days_overdue))));
  if (str(params.deadline)) set("dunning.deadline", rechnungsDatum(String(params.deadline)));
  if (params.fee_cents !== undefined && params.fee_cents !== null && isCurrency(params.currency)) set("dunning.fee", formatMoney(Number(params.fee_cents), params.currency, ctx.locale));
  // 0238 (K-140): Stufe und Verzugszinsen; Zinsen nur, wenn die Mahnung welche berechnet
  set("dunning.stage", str(params.stage_label));
  if (Number(params.interest_cents ?? 0) > 0 && isCurrency(params.currency)) set("dunning.interest", formatMoney(Number(params.interest_cents), params.currency, ctx.locale));
  // 0286 (K-405): Bezeichnung der Gebühr (wie app.mail_have_keys: nur mit Text)
  set("dunning.fee_label", str(params.fee_label));
  // 0305 (K-420): Satzteil mit Artikel und Betrag, aus der Grundlage der Gebühr (fee_basis); ohne Grundlage „eine Mahngebühr von …“
  if (params.fee_cents !== undefined && params.fee_cents !== null && isCurrency(params.currency)) {
    set("dunning.fee_phrase", dunningFeePhrase(feeBasisOf(params.fee_basis), ctx.language === "en" ? "en" : "de", formatMoney(Number(params.fee_cents), params.currency, ctx.locale)));
  }
  // Aufgaben-Mails ans Team (0035)
  set("task.title", str(params.task_title));
  set("task.detail", str(params.task_detail));
  set("member.first_name", str(params.member_first_name));
  // Chat-Sammelmail und Erinnerungen (0041)
  if (params.chat_count !== undefined && params.chat_count !== null) set("chat.count", String(Math.max(0, Number(params.chat_count))));
  set("chat.from", str(params.chat_from));
  set("chat.preview", str(params.chat_preview));
  if (str(params.deadline_date)) set("deadline.date", formatContractDate(String(params.deadline_date), ctx.locale));
  if (params.deadline_days !== undefined && params.deadline_days !== null) set("deadline.days", String(Math.max(0, Number(params.deadline_days))));
  // 0056: was bisher galt (Termin geändert, Deadline geändert) und die tägliche Sammelmail
  if (str(params.previous_date)) {
    const t = eventTimeText({ date: params.previous_date, start_time: params.previous_start_time, time_zone: params.previous_time_zone ?? params.time_zone },
      ctx.language, ctx.recipientTimeZone ?? null);
    set("event.previous", `${formatContractDate(String(params.previous_date), ctx.locale)}${t ? `, ${t}` : ""}`);
  }
  if (str(params.previous_deadline)) set("deadline.previous", formatContractDate(String(params.previous_deadline), ctx.locale));
  if (params.task_count !== undefined && params.task_count !== null) set("task.count", String(Math.max(0, Number(params.task_count))));
  set("task.list", str(params.task_list));
  // 0072: Angebot an die Marke
  set("offer.number", str(params.offer_number));
  set("offer.title", str(params.offer_title));
  if (str(params.valid_until)) set("offer.valid_until", formatContractDate(String(params.valid_until), ctx.locale));
  if (params.offer_total_cents !== undefined && params.offer_total_cents !== null && isCurrency(params.currency)) set("offer.amount", formatMoney(Number(params.offer_total_cents), params.currency, ctx.locale));
  set("contact.name", str(params.contact_name));
  // Merkmale
  if (params.invoice_kind === "talent_to_agency" || params.mode === "preconfirmed") out["payment.by_agency"] = "1";
  if (Number(params.items_count) > 0) out["request.items"] = "1";
  if (yes(params.advance)) out["request.advance"] = "1";
  if (yes(params.external)) out["contract.external"] = "1";
  if (ctx.link) out["recipient.account"] = "1";
  if (["CH", "LI"].includes(String(params.vat_country ?? "").toUpperCase())) out["tax.ch"] = "1";
  if (["CH", "LI"].includes(String(params.agency_country ?? "").toUpperCase())) out["agency.ch"] = "1";
  if (String(params.agency_country ?? "").toUpperCase() === "DE") out["agency.de"] = "1";
  if (String(params.agency_country ?? "").toUpperCase() === "AT") out["agency.at"] = "1";
  if (yes(params.old_terms_rule)) out["terms.old"] = "1";
  return out;
}

export interface RenderedMail {
  subject: string;
  html: string;
  /** Platzhalter ohne Wert oder unbekannt; nicht leer = nicht versenden */
  unresolved: string[];
}

const KEYS = new Set<string>([...MAIL_PLACEHOLDERS, ...MAIL_FLAGS]);
const SECTION = /\{\{\s*([#^])\s*([a-z_.]+)\s*\}\}((?:(?!\{\{\s*[#^/])[\s\S])*?)\{\{\s*\/\s*\2\s*\}\}/;

/** Bedingte Textteile auflösen (innen zuerst). Unbekannte Bedingungen landen in `unknown`. */
export function resolveSections(text: string, has: (key: string) => boolean, unknown?: Set<string>): string {
  let s = text;
  for (let guard = 0; guard < 200; guard++) {
    const m = SECTION.exec(s);
    if (!m) break;
    const [all, kind, key, inner] = m as unknown as [string, string, string, string];
    if (!KEYS.has(key)) unknown?.add(key);
    const show = kind === "#" ? has(key) : !has(key);
    s = s.slice(0, m.index) + (show ? inner : "") + s.slice(m.index + all.length);
  }
  // Übrig gebliebene, nicht geschlossene Marken melden statt stehen lassen
  for (const m of s.matchAll(/\{\{\s*[#^/]\s*([a-z_.]+)\s*\}\}/g)) unknown?.add(`${m[1]} (nicht geschlossen)`);
  return s.replace(/\{\{\s*[#^/]\s*[a-z_.]+\s*\}\}/g, "");
}

/** Leere Absätze entfernen, die nach dem Auflösen bedingter Teile übrig bleiben. */
function tidy(html: string): string {
  return html.replace(/<p>\s*<\/p>\s*/g, "").replace(/\n{3,}/g, "\n\n");
}

export function renderMailText(template: { subject: string; body: string }, vars: Partial<Record<MailKey, string>>): RenderedMail {
  const unresolved = new Set<string>();
  const has = (k: string) => {
    const v = (vars as Record<string, string | undefined>)[k];
    return v !== undefined && v !== "";
  };
  const fill = (text: string, html: boolean) =>
    resolveSections(text, has, unresolved).replace(/\{\{\s*([a-z_.]+)\s*\}\}/g, (m, key: string) => {
      const v = (vars as Record<string, string | undefined>)[key];
      if (!(MAIL_PLACEHOLDERS as readonly string[]).includes(key) || v === undefined) {
        unresolved.add(key);
        return m;
      }
      return html ? esc(v).replace(/\r?\n/g, "<br>") : v.replace(/[\r\n]+/g, " ");
    });
  const subject = fill(template.subject, false).replace(/\s{2,}/g, " ").trim();
  const html = tidy(fill(template.body, true));
  return { subject, html, unresolved: [...unresolved] };
}

/**
 * Eigener Text für genau einen Versand (0161, Mahnung): als Klartext bearbeitet (Absätze durch Leerzeilen, Zeilenumbruch
 * bleibt), für die Mail in Absätze gesetzt und immer escapt. Anders als Vorlagen (nur Admins) schreibt ihn jeder mit dem Recht
 * „Mahnen“; HTML gelangt so nie ungeprüft in eine Mail an die Marke.
 */
export function plainTextToMailHtml(text: string): string {
  return text.replace(/\r\n?/g, "\n").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("\n");
}

/** Gerenderte Mail als Klartext zum Bearbeiten (Absätze durch Leerzeilen getrennt); Gegenstück zu plainTextToMailHtml. */
export function mailHtmlToPlainText(html: string): string {
  return html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n\n").replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&#39;|&#x27;|&apos;/g, "'").replace(/&amp;/g, "&")
    .replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Absätze, die nur den Portal-Link tragen (`<p><a href="{{link}}">{{link}}</a></p>` oder `<p>{{link}}</p>`), aus einer
 * Vorlage entfernen: für Empfänger ohne Konto (Marken), deren eigene Agentur-Fassung noch einen Link enthält.
 */
export function stripLinkParagraphs(body: string): string {
  return body.replace(/<p>\s*(?:<a\s[^>]*href=(["'])\{\{\s*link\s*\}\}\1[^>]*>[\s\S]*?<\/a>|\{\{\s*link\s*\}\})\s*<\/p>\s*/gi, "");
}

// ── Mailarten: Bezeichnung, Werte je Mail, Beispielwerte (Vorlagen-Editor, Postausgang; Befunde 19, 20) ─────────────

/** Bezeichnung jeder Mail für Auswahllisten und den Postausgang. Unbekannte Schlüssel (eigene Texte) zeigen ihren Schlüssel. */
export const MAIL_TEXT_LABELS: Readonly<Record<string, string>> = {
  "dunning.reminder": "Zahlungserinnerung",
  "dunning.first_notice": "1. Mahnung",
  "dunning.second_notice": "2. Mahnung",
  "dunning.final_notice": "Letzte Mahnung",
  "dunning.collection_notice": "Übergabe an Inkasso",
  "email.invite_member": "Einladung ins Team",
  "email.invite_talent": "Einladung für Creator",
  "email.contract_to_sign": "Vertrag zur Unterschrift",
  "email.contract_signed": "Vertrag unterschrieben",
  "email.contract_reminder": "Vertrag: Erinnerung an die Unterschrift",
  "email.contract_copy": "Vertrag: Ausfertigung (Leselink)",
  "email.contract_stopped": "Vertrag angehalten",
  "email.contract_voided": "Vertrag aufgehoben",
  "email.contract_terminated": "Vertrag gekündigt",
  "email.invoice_to_brand": "Rechnung an Marke",
  "email.offer_to_brand": "Angebot an Marke",
  "email.credit_note_to_brand": "Stornorechnung an Marke",
  "email.agency_invoice_issued": "Rechnung der Agentur an den Creator",
  "email.self_billing_issued": "Gutschrift an den Creator",
  "email.self_billing_cancelled": "Gutschrift storniert (Stornobeleg an den Creator)",
  "email.invoice_cancelled": "Rechnung storniert (an den Creator)",
  "email.invoice_collection": "Rechnung beim Inkasso (an den Creator)",
  "email.invoice_collection_withdrawn": "Inkasso zurückgenommen (an den Creator)",
  "email.invoice_request": "Rechnung anfordern",
  "email.invoice_revision": "Rechnung korrigieren",
  "email.payment_check": "Zahlungsnachfrage beim Creator",
  "email.payment_confirmed": "Zahlung bestätigt",
  "email.payment_reset": "Zahlung zurückgesetzt",
  "email.payout_revoked": "Auszahlung zurückgenommen",
  "email.bank_details_changed": "Bankverbindung geändert (Schutzmail an den Creator)",
  "email.calendar_event": "Neuer Termin",
  "email.calendar_event_changed": "Termin geändert",
  "email.calendar_event_cancelled": "Termin abgesagt",
  "email.content_deadline_soon": "Erinnerung: Content-Deadline naht",
  "email.content_deadline_changed": "Deadline geändert",
  "email.deliverable_draft_due": "Erinnerung: Entwurf morgen fällig",
  "email.deliverable_publish_due": "Erinnerung: Veröffentlichung morgen",
  "email.deliverable_overdue_talent": "Beitrag überfällig (an den Creator)",
  "email.brand_review_request": "Freigabe-Link an die Marke",
  "email.brand_report": "Bericht an die Marke",
  "email.brand_approved": "Marke hat freigegeben",
  "email.brand_changes": "Änderungswunsch der Marke",
  "email.content_changes": "Korrektur am Entwurf (an den Creator, immer per Mail)",
  "email.deliverable_metrics_due": "Kennzahlen hochladen",
  "email.chat_unread": "Ungelesene Nachrichten (Sammelmail)",
  "email.staff_task": "Aufgabe für das Team",
  "email.task_digest": "Tagesübersicht (Sammelmail statt Einzelmails)",
  "email.insights_share": "Insights-Freigabe an eine Marke",
  "email.media_kit": "Media-Kit bzw. Pitch-Liste an eine Marke",
  "email.guardian_consent": "Hinweis an Erziehungsberechtigte (Einwilligung beim Konto-Anlegen)",
  // 0518: Bestätigungslink an die Eltern, Rückmeldung an den Creator
  "email.guardian_confirm": "Bestätigungslink an Erziehungsberechtigte (Einwilligung vor dem Zugang)",
  "email.guardian_decided": "Rückmeldung der Erziehungsberechtigten (an den Creator)",
  "email.org_cancel_confirmed": "Kündigung bestätigt (Plattform)",
  // 0252: Kündigung in der Testphase wirkt sofort
  "email.org_trial_cancelled": "Testphase beendet (Plattform)",
  // 0171: einmal 14 Tage nach dem Ende des Abos, mit Code ZURUECK30
  "email.org_winback": "Rückgewinnung nach der Kündigung (Plattform an die Agentur)",
  "email.org_purge_notice": "Löschung angekündigt (Plattform)",
  // 0077 Korrekturrunde 1
  "email.org_readonly_notice": "Agentur nur noch lesbar (Plattform an die Creator)",
  "email.mfa_reset": "Zweiter Faktor zurückgesetzt (Plattform an die Person)",
  "email.member_mfa_reset": "Zweiter Faktor zurückgesetzt (Plattform an die Admins)",
  "email.contract_resumed": "Vertrag läuft weiter",
  "email.tax_status_check": "Steuerstatus fürs neue Jahr bestätigen",
  "email.invoice_rejection_reverted": "Ablehnung zurückgenommen",
  "email.deliverable_deadline_changed": "Termin eines Beitrags geändert",
  // 0085 Korrekturrunde 2
  "email.deliverable_added": "Neuer Beitrag (an den Creator)",
  // 0458 Prüf-Chat CP-014: neue Kooperation an die Creatorin
  "email.deal_new": "Neue Kooperation (an den Creator)",
  "email.deliverable_cancelled": "Beitrag entfällt (an den Creator)",
  // 0092 Korrekturrunde 3
  "email.invite_chef": "Einladung als Admin und Inhaber (Plattform)",
  "email.org_subscription_expiring": "Abo-Laufzeit endet bald (Plattform an die Agentur)",
  "email.org_subscription_expired": "Abo-Laufzeit abgelaufen (Plattform an die Agentur)",
  // 0187: fehlgeschlagene Abo-Zahlung (Stripe) in Textform
  "email.org_subscription_payment_failed": "Abo-Zahlung fehlgeschlagen (Plattform an die Admins)",
  // 0101 Betrieb: Mails an den Plattform-Betrieb (support_email)
  "email.platform_ops_report": "Täglicher Betriebsbericht (an den Plattform-Betrieb)",
  "email.platform_ops_alert": "Sofortwarnung (an den Plattform-Betrieb)",
  // 0243: offene Registrierung (Hinweis an den Betrieb) und Erinnerung an die Person mit Ein-Klick-Link
  "email.platform_signup_open": "Neue Registrierung, noch nicht bestätigt (an den Plattform-Betrieb)",
  "email.signup_reminder": "Erinnerung: Adresse bestätigen (Plattform an die registrierte Person)",
  "email.platform_signup_confirmed": "Registrierung über die Erinnerung bestätigt (an den Plattform-Betrieb)",
  // 0390: Demo-Termine (Erinnerung, Absage durch den Betrieb, Link nachgetragen; Buchung selbst verschickt die Serverfunktion)
  "email.platform_demo": "Demo-Termin (Plattform an die buchende Person)",
  "email.platform_demo_ops": "Demo-Termin (an den Plattform-Betrieb)",
  // 0102 eigene Absenderdomain: bestätigt, gescheitert, entfernt (Plattform an die Admins)
  "email.sender_domain_status": "Eigene Absenderdomain: Stand (Plattform an die Admins)",
};
export function mailTextLabel(key: string): string {
  return MAIL_TEXT_LABELS[key] ?? (key.startsWith("dunning.") ? "Mahnung" : key);
}

const INVOICE_KEYS: MailKey[] = ["invoice.number", "invoice.amount", "invoice.due_date", "brand.name", "deal.title", "period.month"];
const TALENT_KEYS: MailKey[] = ["talent.first_name", "link", "recipient.account"];
const EVENT_KEYS: MailKey[] = ["event.title", "event.date", "event.time", "event.location", "event.meeting_url", "event.description"];
const DUNNING_KEYS: MailKey[] = [...INVOICE_KEYS, "invoice.days_overdue", "invoice.paid_amount", "invoice.open_amount", "dunning.fee", "dunning.fee_label", "dunning.fee_phrase", "dunning.deadline", "dunning.stage", "dunning.interest", "creditor.name", "reason", "agency.ch", "agency.de", "agency.at"];

/**
 * Welche Werte eine Mail überhaupt füllen kann (aus den Parametern der jeweiligen Auslöser). Der Vorlagen-Editor bietet nur
 * diese Platzhalter an und warnt, wenn eine eigene Fassung andere nutzt: beim Versand gilt dann der Standardtext.
 * `agency.name` gibt es immer. Unbekannte Schlüssel (neue Mailarten): alle Platzhalter.
 */
export const MAIL_TEXT_FIELDS: Readonly<Record<string, readonly MailKey[]>> = {
  "dunning.reminder": DUNNING_KEYS, "dunning.first_notice": DUNNING_KEYS, "dunning.second_notice": DUNNING_KEYS,
  "dunning.final_notice": DUNNING_KEYS, "dunning.collection_notice": DUNNING_KEYS,
  "email.invite_member": ["link", "member.first_name", "deadline.date"],   // Prüf-Chat CP-007 (02.11.2026): Gültigkeit mit Datum
  "email.invite_talent": ["link", "talent.first_name", "deadline.date"],
  "email.contract_to_sign": ["contract.title", "link", "talent.first_name", "reason", "contact.name", "deadline.date"],   // 0222: Anrede mit Namen, Gültigkeit mit Datum
  "email.contract_signed": ["contract.title", "link", "contract.external"],
  "email.contract_reminder": ["contract.title", "link", "event.date"],
  "email.contract_copy": ["contract.title", "link"],
  "email.contract_stopped": ["contract.title", "reason"],
  "email.contract_voided": ["contract.title", "reason"],
  "email.contract_terminated": ["contract.title", "event.date", "reason"],
  "email.invoice_to_brand": INVOICE_KEYS,
  "email.offer_to_brand": ["brand.name", "offer.number", "offer.title", "offer.valid_until", "offer.amount", "contact.name"],
  "email.credit_note_to_brand": [...INVOICE_KEYS, "reason"],
  "email.agency_invoice_issued": [...TALENT_KEYS, ...INVOICE_KEYS, "reason"],
  "email.self_billing_issued": [...TALENT_KEYS, ...INVOICE_KEYS, "reason"],
  "email.self_billing_cancelled": [...TALENT_KEYS, ...INVOICE_KEYS, "reason"],
  "email.invoice_cancelled": [...TALENT_KEYS, ...INVOICE_KEYS, "reason"],
  "email.invoice_collection": [...TALENT_KEYS, ...INVOICE_KEYS],
  // 0094: Übergabe an das Inkasso zurückgenommen (AUTOMATIK-R3-2)
  "email.invoice_collection_withdrawn": [...TALENT_KEYS, ...INVOICE_KEYS],
  "email.invoice_request": [...TALENT_KEYS, "brand.name", "deal.title", "period.month", "reason", "request.advance", "request.items"],
  "email.invoice_revision": [...TALENT_KEYS, "invoice.number", "invoice.amount", "brand.name", "deal.title", "period.month", "reason"],
  "email.payment_check": [...TALENT_KEYS, ...INVOICE_KEYS, "reason", "payment.by_agency"],
  "email.payment_confirmed": [...TALENT_KEYS, ...INVOICE_KEYS, "payment.by_agency"],
  "email.payment_reset": [...TALENT_KEYS, ...INVOICE_KEYS],
  "email.payout_revoked": [...TALENT_KEYS, ...INVOICE_KEYS],
  "email.bank_details_changed": [...TALENT_KEYS, "reason"],
  "email.calendar_event": [...TALENT_KEYS, ...EVENT_KEYS],
  "email.calendar_event_changed": [...TALENT_KEYS, ...EVENT_KEYS, "event.previous"],
  "email.calendar_event_cancelled": [...TALENT_KEYS, ...EVENT_KEYS],
  "email.content_deadline_soon": [...TALENT_KEYS, "brand.name", "deal.title", "period.month", "deadline.date", "deadline.days"],
  "email.content_deadline_changed": [...TALENT_KEYS, "brand.name", "deal.title", "period.month", "deadline.date", "deadline.previous"],
  "email.deliverable_draft_due": [...TALENT_KEYS, "brand.name", "deal.title", "deadline.date", "deadline.days"],
  "email.deliverable_publish_due": [...TALENT_KEYS, "brand.name", "deal.title", "deadline.date", "deadline.days"],
  "email.deliverable_overdue_talent": [...TALENT_KEYS, "brand.name", "deal.title"],
  // 0073: Marke ohne Konto (Link im Text), Creator mit Portal-Link
  "email.brand_review_request": ["link", "brand.name", "deal.title", "task.detail", "event.date"],
  "email.brand_report": ["link", "brand.name", "task.title", "event.date"],
  "email.brand_approved": [...TALENT_KEYS, "brand.name", "deal.title", "task.detail", "reason"],
  "email.brand_changes": [...TALENT_KEYS, "brand.name", "deal.title", "task.detail", "reason"],
  // 0155: Korrektur der Agentur mit Zahl der Kommentare an der Datei
  "email.content_changes": [...TALENT_KEYS, "brand.name", "deal.title", "task.detail", "reason", "task.count"],
  "email.deliverable_metrics_due": [...TALENT_KEYS, "brand.name", "deal.title", "task.detail", "deadline.date", "deadline.days"],
  "email.chat_unread": [...TALENT_KEYS, "chat.count", "chat.from", "chat.preview"],
  "email.staff_task": ["link", "member.first_name", "task.title", "task.detail"],
  "email.task_digest": ["link", "recipient.account", "member.first_name", "talent.first_name", "task.count", "task.list"],
  "email.insights_share": ["link", "event.date", "task.detail"],
  "email.media_kit": ["link", "event.date", "task.detail"],
  // 0079: an die erziehungsberechtigte Person eines minderjährigen Creators, ohne Link
  "email.guardian_consent": ["event.date", "task.detail"],
  // 0518: Link /einwilligung?t=… gültig bis zum Ablauf der Einladung; Rückmeldung mit dem Satz in task.detail
  "email.guardian_confirm": ["link", "event.date", "task.detail", "deadline.date"],
  "email.guardian_decided": ["link", "task.detail"],
  // 0433: in einer laufenden Pause der Satz „Bis dahin bleibt das Konto pausiert …“ (task.detail)
  "email.org_cancel_confirmed": ["link", "event.date", "task.detail", "terms.old"],
  // 0437: Wortlaut vor 0425 bei alter Regel (terms.old)
  "email.org_trial_cancelled": ["link", "event.date", "terms.old"],
  "email.org_winback": ["link", "terms.old"],
  "email.org_purge_notice": ["link", "event.date"],
  // 0077 Korrekturrunde 1
  "email.org_readonly_notice": [...TALENT_KEYS, "deadline.date", "portal.name"],
  "email.mfa_reset": ["link", "reason"],
  "email.member_mfa_reset": ["link", "reason"],
  "email.contract_resumed": [...TALENT_KEYS, "contract.title"],
  "email.tax_status_check": [...TALENT_KEYS, "deadline.date", "tax.ch"],
  // 0087: Ablehnung bzw. Rückgabe zur Korrektur zurückgenommen
  "email.invoice_rejection_reverted": [...TALENT_KEYS, ...INVOICE_KEYS],
  "email.deliverable_deadline_changed": [...TALENT_KEYS, "brand.name", "deal.title", "deadline.date", "deadline.previous", "reason"],
  "email.deliverable_added": [...TALENT_KEYS, "brand.name", "deal.title", "deadline.date", "reason"],
  "email.deal_new": [...TALENT_KEYS, "brand.name", "deal.title"],
  "email.deliverable_cancelled": [...TALENT_KEYS, "brand.name", "deal.title", "deadline.previous", "reason"],
  // 0092: Einladung des Betriebs als Admin (auch in der Nur-Lesen-Phase), Laufzeitende eines von Hand freigeschalteten Abos
  "email.invite_chef": ["link", "member.first_name"],
  "email.org_subscription_expiring": ["link", "event.date", "terms.old"],
  "email.org_subscription_expired": ["link", "event.date", "deadline.date", "terms.old"],
  "email.org_subscription_payment_failed": ["link"],
  // 0101: Betriebsbericht und Sofortwarnung (Tag, Zusammenfassung, Zeilen des Berichts, Link in die Betreiber-Konsole)
  "email.platform_ops_report": ["link", "event.date", "task.title", "task.list"],
  "email.platform_ops_alert": ["link", "event.date", "task.title", "task.list"],
  "email.platform_signup_open": ["link", "event.date", "task.title", "task.list"],
  "email.signup_reminder": ["link", "task.title", "task.detail"],
  "email.platform_signup_confirmed": ["link", "event.date", "task.title", "task.list"],
  "email.platform_demo": ["link", "member.first_name", "task.title", "task.detail", "event.description", "reason", "task.list"],
  "email.platform_demo_ops": ["link", "task.title", "task.detail", "event.description", "reason"],
  // 0102: Überschrift, Domain und Erklärung entstehen je Sprache in der Datenbank (app.sender_domain_mail)
  "email.sender_domain_status": ["link", "task.title", "task.detail", "reason"],
};

/** Platzhalter und Merkmale, die diese Mail füllen kann (mit agency.name und portal.name, 0106); unbekannte Mail: alle. */
export function mailFieldsFor(key: string): MailKey[] {
  const own = MAIL_TEXT_FIELDS[key];
  if (!own) return [...MAIL_PLACEHOLDERS, ...MAIL_FLAGS];
  return [...new Set<MailKey>(["agency.name", "portal.name", ...own])];
}

/**
 * Beispielwerte für die Vorschau im Vorlagen-Editor: jeder Platzhalter hat einen Wert, damit ein unveränderter
 * Standardtext nie „offene Platzhalter“ meldet. Beträge im Format der Agentur (money), Daten relativ zu heute.
 */
export function mailSampleValues(language: Language, agencyName: string, money: (cents: number) => string, today: string): Record<MailKey, string> {
  const d = (n: number) => {
    const x = new Date(`${today}T12:00:00Z`);
    x.setUTCDate(x.getUTCDate() + n);
    const iso = x.toISOString().slice(0, 10);
    return language === "en" ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
  };
  const en = language === "en";
  return {
    "agency.name": agencyName,
    "talent.first_name": en ? "Alex" : "Vorname",
    "brand.name": en ? "Sample brand" : "Beispielmarke",
    "invoice.number": "RE-2026-001",
    "invoice.amount": money(119000),
    "invoice.paid_amount": money(50000),
    "portal.name": portalNameText(null, language),
    "invoice.open_amount": money(69000),
    "invoice.due_date": d(-14),
    "invoice.days_overdue": "14",
    "dunning.fee": money(4000),
    "dunning.deadline": d(7),
    "dunning.stage": en ? "2nd reminder" : "2. Mahnung",
    "dunning.interest": money(1234),
    "dunning.fee_label": en ? "flat late-payment fee (section 288 (5) BGB)" : "Verzugspauschale (§ 288 Abs. 5 BGB)",
    "dunning.fee_phrase": en ? `a ${money(4000)} flat late-payment fee (section 288 (5) BGB)` : `die Verzugspauschale von ${money(4000)} (§ 288 Abs. 5 BGB)`,
    "contract.title": en ? "Collaboration agreement" : "Kooperationsvertrag",
    link: "https://example.com/link",
    "deal.title": en ? "Sample collaboration" : "Beispielkooperation",
    "period.month": "September 2026",
    reason: en ? "Service date missing" : "Leistungsdatum fehlt",
    "event.title": en ? "Shoot" : "Dreh",
    "event.date": d(3),
    "task.title": en ? "Check invoice" : "Rechnung prüfen",
    "task.detail": en ? "Sample creator · RE-2026-001 · Sample brand" : "Beispiel-Creator · RE-2026-001 · Beispielmarke",
    "member.first_name": en ? "Sam" : "Kim",
    "event.time": en ? "10:00–12:00 (Europe/Berlin)" : "10:00–12:00 Uhr (Europe/Berlin)",
    "event.location": "Studio 2",
    "event.meeting_url": "https://meet.example.com/abc-defg-hij",
    "event.description": en ? "Please bring two outfits." : "Bitte zwei Outfits mitbringen.",
    "chat.count": "2",
    "chat.from": agencyName,
    "chat.preview": en ? "Could you send the draft by Friday?" : "Kannst du den Entwurf bis Freitag schicken?",
    "deadline.date": d(2),
    "deadline.days": "2",
    "creditor.name": en ? "Sample creator" : "Beispiel-Creator",
    "event.previous": `${d(1)}, ${en ? "09:00" : "09:00 Uhr"}`,
    "deadline.previous": d(-1),
    "task.count": "2",
    "task.list": en ? "• Check invoice · RE-2026-001\n• Contract awaits your signature" : "• Rechnung prüfen · RE-2026-001\n• Vertrag wartet auf deine Unterschrift",
    "offer.number": "AN-2026-001",
    "offer.title": en ? "Autumn campaign" : "Herbstkampagne",
    "offer.valid_until": d(14),
    "offer.amount": money(450000),
    "contact.name": en ? "Robin" : "Kim",
    "payment.by_agency": "1",
    "request.items": "1",
    "request.advance": "",
    "contract.external": "",
    "recipient.account": "1",
    "tax.ch": "",
    "agency.ch": "",
    "agency.de": "",
    "agency.at": "",
    "terms.old": "",
  };
}

/** Platzhalter in Betreff und Text, die diese Mail nie füllt (eigene Fassung: der Versand nimmt dann den Standardtext). */
export function unsupportedPlaceholders(key: string, subject: string, body: string): string[] {
  const allowed = new Set<string>(mailFieldsFor(key));
  const used = new Set<string>();
  for (const m of `${subject}\n${body}`.matchAll(/\{\{\s*[#^]?\s*([a-z_.]+)\s*\}\}/g)) used.add(m[1]!);
  return [...used].filter((k) => KEYS.has(k) && !allowed.has(k));
}

/** Grundlage der Gebühr aus den Mailparametern (fee_basis, 0305); alles andere gilt als „keine Grundlage“. */
export function feeBasisOf(v: unknown): { kind: "flat" | "stage_credited"; law: string | null } | null {
  if (!v || typeof v !== "object") return null;
  const o = v as { kind?: unknown; law?: unknown };
  if (o.kind !== "flat" && o.kind !== "stage_credited") return null;
  return { kind: o.kind, law: typeof o.law === "string" && o.law.trim() ? o.law.trim().toUpperCase() : null };
}

/**
 * Gebühr als Satzteil mit Artikel und Betrag (K-420, 0305), für „Zusätzlich berechnen wir {{dunning.fee_phrase}}.“ bzw.
 * „For this notice we charge {{dunning.fee_phrase}}.“. Bezeichnungen wie in der Forderungsaufstellung (feeLabel): Verzugspauschale
 * nach § 288 Abs. 5 BGB, in Österreich Pauschale für Betreibungskosten (§ 458 UGB), angerechnete Stufengebühren als Mahngebühren
 * bzw. Mahnspesen, in der Schweiz und in Liechtenstein Mahngebühr. `amount` ist der fertig formatierte Betrag.
 */
export function dunningFeePhrase(basis: { kind: "flat" | "stage_credited"; law: string | null } | null | undefined, lang: "de" | "en", amount: string): string {
  const law = basis?.law?.toUpperCase() ?? null;
  const en = lang === "en";
  if (!basis || law === "CH" || law === "LI") return en ? `a ${amount} dunning fee` : `eine Mahngebühr von ${amount}`;
  if (basis.kind === "flat") {
    if (law === "AT") return en ? `a ${amount} flat compensation for recovery costs (section 458 UGB)` : `die Pauschale für Betreibungskosten von ${amount} (§ 458 UGB)`;
    if (law === "DE") return en ? `a ${amount} flat late-payment fee (section 288 (5) BGB)` : `die Verzugspauschale von ${amount} (§ 288 Abs. 5 BGB)`;
    return en ? `a ${amount} flat late-payment fee` : `die Verzugspauschale von ${amount}`;
  }
  if (law === "AT") return en ? `${amount} in dunning fees (the flat compensation for recovery costs is credited against them)` : `Mahnspesen von ${amount} (die Pauschale für Betreibungskosten ist darauf angerechnet)`;
  return en ? `${amount} in dunning fees (the flat late-payment fee is credited against them)` : `Mahngebühren von ${amount} (die Verzugspauschale ist darauf angerechnet)`;
}

/** Datum im englischen Schreib- und Mailformat „10 Aug 2026“ (wie fmtDate im Mahnschreiben); Unlesbares bleibt unverändert */
export function englishDate(value: string): string {
  const iso = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return value;
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${iso}T12:00:00Z`));
}
